import {
  CREATE_DATASOURCE,
  DELETE_DATASOURCE,
  FETCH_DATASOURCE_BY_ID,
  FETCH_DATASOURCES,
  UPDATE_DATASOURCE,
} from "@apiUtils";
import { mutationFetch } from "@atlas/sdk";
import type { IHubDataSource } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const HUB_DATASOURCES_KEY = "hubDataSourcesKey";
export const HUB_DATASOURCE_BY_ID_KEY = "hubDataSourceByIdKey";

export const getHubDataSourcesKey = () => [HUB_DATASOURCES_KEY] as const;

export const getHubDataSourceByIdKey = (datasourceId: number) =>
  [HUB_DATASOURCE_BY_ID_KEY, datasourceId] as const;

export const useHubDataSources = (
  options?: Partial<
    UseQueryOptions<
      IHubDataSource[],
      AxiosError,
      IHubDataSource[],
      ReturnType<typeof getHubDataSourcesKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubDataSourcesKey(),
    queryFn: async () => {
      return mutationFetch<IHubDataSource[]>({
        url: FETCH_DATASOURCES,
        method: "GET",
      });
    },
    ...options,
  });
};

export const useHubDataSource = (
  datasourceId: number,
  options?: Partial<
    UseQueryOptions<
      IHubDataSource,
      AxiosError,
      IHubDataSource,
      ReturnType<typeof getHubDataSourceByIdKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubDataSourceByIdKey(datasourceId),
    queryFn: async () => {
      return mutationFetch<IHubDataSource>({
        url: FETCH_DATASOURCE_BY_ID.replace(
          ":datasourceId",
          String(datasourceId),
        ),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && datasourceId > 0,
  });
};

const createHubDataSource = (body: IHubDataSource): Promise<IHubDataSource> => {
  return mutationFetch<IHubDataSource>({
    url: CREATE_DATASOURCE,
    method: "POST",
    body,
  });
};

const updateHubDataSource = (
  datasourceId: number,
  body: IHubDataSource,
): Promise<IHubDataSource> => {
  return mutationFetch<IHubDataSource>({
    url: UPDATE_DATASOURCE.replace(":datasourceId", String(datasourceId)),
    method: "PUT",
    body,
  });
};

const deleteHubDataSource = (datasourceId: number): Promise<void> => {
  return mutationFetch<void>({
    url: DELETE_DATASOURCE.replace(":datasourceId", String(datasourceId)),
    method: "DELETE",
  });
};

export const useCreateHubDataSource = (
  options?: UseMutationOptions<IHubDataSource, AxiosError, IHubDataSource>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubDataSource,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [HUB_DATASOURCES_KEY] });
      if (data.datasourceId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubDataSourceByIdKey(data.datasourceId),
        });
      }
    },
    ...options,
  });
};

export const useUpdateHubDataSource = (
  options?: UseMutationOptions<IHubDataSource, AxiosError, IHubDataSource>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: IHubDataSource) => {
      const datasourceId = body.datasourceId;
      if (datasourceId == null) {
        throw new Error("datasourceId is required for update");
      }
      return updateHubDataSource(datasourceId, body);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [HUB_DATASOURCES_KEY] });
      if (data.datasourceId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubDataSourceByIdKey(data.datasourceId),
        });
      }
    },
    ...options,
  });
};

export const useDeleteHubDataSource = (
  options?: UseMutationOptions<void, AxiosError, number>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHubDataSource,
    onSuccess: (_, datasourceId) => {
      queryClient.invalidateQueries({ queryKey: [HUB_DATASOURCES_KEY] });
      queryClient.invalidateQueries({
        queryKey: getHubDataSourceByIdKey(datasourceId),
      });
    },
    ...options,
  });
};
